import { useState, useMemo, memo, useRef } from 'react';
import { FileText, Plus, X, Edit2, Trash2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { CasoShareSection } from './CasoShareSection';

interface CasoDocument {
  id: string;
  caso_id: string;
  title: string;
  type: string;
  content: string;
  updated_at: string;
  created_at: string;
}

interface CasoDocumentsPanelProps {
  documents: CasoDocument[];
  activeDocumentId?: string;
  onDocumentClick?: (documentId: string) => void;
  onNewDocument?: () => void;
  onEditDocument?: (documentId: string) => void;
  onDeleteDocument?: (documentId: string) => void;
  isLoading?: boolean;
  casoId: string;
  casoTitle: string;
}

export const CasoDocumentsPanel = memo(function CasoDocumentsPanel({
  documents,
  activeDocumentId,
  onDocumentClick,
  onNewDocument,
  onEditDocument,
  onDeleteDocument,
  isLoading = false,
  casoId,
  casoTitle,
}: CasoDocumentsPanelProps) {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: ptBR,
      });
    } catch {
      return 'Data inválida';
    }
  };

  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents;
    
    const query = searchQuery.toLowerCase();
    return documents.filter(doc => 
      doc.title.toLowerCase().includes(query) ||
      doc.type.toLowerCase().includes(query)
    );
  }, [documents, searchQuery]);

  const DocumentsList = () => (
    <div className="caso-docs-container">
      <CasoShareSection casoId={casoId} casoTitle={casoTitle} />
      
      <div className="caso-docs-header">
        <h3 className="caso-docs-title">Documentos do Caso</h3>
        <Button
          onClick={onNewDocument}
          size="sm"
          className="caso-docs-new-btn"
          disabled={isLoading}
        >
          <Plus className="h-4 w-4 mr-1" />
          Novo
        </Button>
      </div>

      {/* Campo de busca */}
      <div className="caso-docs-search" data-tour="caso-search-docs">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar documentos..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                // Garantir que o foco permanece após onChange
                requestAnimationFrame(() => {
                  searchInputRef.current?.focus();
                });
              }}
              onFocus={() => console.log('[CASO-SEARCH] Input focado')}
              onBlur={(e) => console.log('[CASO-SEARCH] Input perdeu foco:', e.relatedTarget)}
              className="pl-9 h-9 text-sm"
              data-caso-search="true"
            />
        </div>
      </div>

      <ScrollArea className="caso-docs-scroll" data-tour="caso-docs-list">
        <div className="caso-docs-list">
          {isLoading ? (
            <div className="space-y-3 p-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="caso-docs-card border-none bg-transparent">
                  <Skeleton className="h-8 w-8 rounded-md" />
                  <div className="caso-docs-card-content flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="caso-docs-empty">
              <FileText className="h-12 w-12 text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">
                {searchQuery ? 'Nenhum documento encontrado' : 'Nenhum documento criado'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Clique em "Novo" para começar
              </p>
            </div>
          ) : (
            filteredDocuments.map((doc) => (
              <div
                key={doc.id}
                className={`caso-docs-card ${
                  activeDocumentId === doc.id ? 'caso-docs-card-active' : ''
                }`}
              >
                <button
                  onClick={() => {
                    onDocumentClick?.(doc.id);
                    if (isMobile) setIsOpen(false);
                  }}
                  className="caso-docs-card-clickable"
                >
                  <div className="caso-docs-card-icon">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="caso-docs-card-content">
                    <h4 className="caso-docs-card-title">{doc.title}</h4>
                    <p className="caso-docs-card-type">{doc.type}</p>
                    <p className="caso-docs-card-date">{formatDate(doc.updated_at)}</p>
                  </div>
                </button>
                
                <div className="caso-docs-card-actions">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="caso-docs-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditDocument?.(doc.id);
                    }}
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="caso-docs-action-btn caso-docs-delete-btn"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja excluir o documento "{doc.title}"? Esta ação não pode ser desfeita.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteDocument?.(doc.id);
                          }}
                        >
                          Excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );

  // Mobile: Sheet/Drawer
  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="fixed bottom-20 right-4 z-50 shadow-lg"
          >
            <FileText className="h-4 w-4 mr-2" />
            Documentos ({documents.length})
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="h-[80vh]">
          <SheetHeader>
            <SheetTitle>Documentos do Caso</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <DocumentsList />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Coluna lateral fixa
  return (
    <aside className="caso-docs-sidebar">
      <DocumentsList />
    </aside>
  );
});
